import { apiFetch, setStoredToken } from "./client";

const BASE = "/auth/patient";

export type PatientUser = {
  id?: string;
  email?: string;
  nip?: string;
  nom?: string;
  prenom?: string;
  date_naissance?: string;
  type?: string;
  statut?: string;
  profile?: Record<string, unknown>;
  [key: string]: unknown;
};

export type ProfileResponse = PatientUser;

export type PatientSignupBody = {
  email: string;
  password: string;
  confirmPassword: string;
  profileData: {
    nip: string;
    nom: string;
    prenom: string;
    date_naissance: string;
  };
};

export type PatientSignupInfo = {
  signupAllowed?: boolean;
  requirements?: string[];
  actorType?: string;
  [key: string]: unknown;
};

export type PatientConsultation = {
  id?: string;
  patientNip?: string;
  professionnelNip?: string;
  etablissementId?: string;
  date?: string;
  typeConsultation?: string;
  motif?: string;
  observations?: string;
  documents?: unknown[];
  complement?: unknown;
  statutConsultation?: string;
  etatSortie?: string;
  dateCloture?: string;
  [key: string]: unknown;
};

export type PatientPrescription = {
  id?: string;
  patientNip?: string;
  consultationId?: string;
  professionnelNip?: string;
  etablissementId?: string;
  datePrescription?: string;
  date?: string;
  instructions?: string;
  text?: string;
  statut?: string;
  medicaments?: unknown[];
  documents?: unknown[];
  [key: string]: unknown;
};

export type PatientVaccination = {
  id?: string;
  patientNip?: string;
  professionnelNip?: string;
  etablissementId?: string;
  typeVaccin?: string;
  libelleVaccin?: string;
  vaccin?: string;
  dateAdministration?: string;
  dateVaccination?: string;
  dose?: string;
  lot?: string;
  siteInjection?: string;
  prochaineDose?: string | null;
  prochaineDoseType?: string | null;
  [key: string]: unknown;
};

export type CreatePatientVaccinationBody = {
  patientNip: string;
  vaccin: string;
  typeVaccin?: string;
  dateVaccination?: string;
  dose?: string;
};

export type PatientMedicalFilePersonalInfo = {
  nom?: string;
  prenom?: string;
  dateNaissance?: string;
  adresse?: string;
  email?: string;
  telephone?: string;
  profession?: string;
};

export type PatientMedicalFileMedicalInfo = {
  poids?: number;
  taille?: number;
  groupeSanguin?: string;
  electrophorese?: string;
  allergies?: string[];
  intolerances?: string[];
  vaccinations?: Array<{ type?: string; date?: string; professionnel?: string }>;
  antecedents?: unknown[];
};

export type PatientMedicalFileSnapshot = {
  address?: string;
  birth?: string;
  electro?: string;
  gs?: string;
  poids?: number;
  taille?: number;
  allergies?: string;
  intolerances?: string;
  vaccins?: string[];
  coverage?: "verification" | "non" | "oui";
};

export type PatientMedicalFile = {
  /** `false` pour un patient nouvellement inscrit qui n'a pas encore de dossier (créé par un professionnel lors d'une consultation). */
  existe?: boolean;
  id?: string;
  numeroDossier?: string;
  patientNip?: string;
  statut?: string;
  informationsPersonnelles?: PatientMedicalFilePersonalInfo;
  informationsMedicales?: PatientMedicalFileMedicalInfo;
  snapshot?: PatientMedicalFileSnapshot;
};

export type UpdatePatientMedicalFileBody = {
  informationsPersonnelles: PatientMedicalFilePersonalInfo;
};

/** Corps JSON plat pour `PUT /patient/patients/:nip` (champs optionnels, alignés sur UpdatePatientData backend). */
export type UpdatePatientData = {
  numeroDossier?: string;
  nom?: string;
  prenom?: string;
  dateNaissance?: string;
  date_naissance?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  poids?: number;
  taille?: number;
  groupeSanguin?: string;
  electrophorese?: string;
  profession?: string;
  couvertureAMO?: "oui" | "non" | "verification";
  typeAssurance?: string;
  statut?: string;
  createur?: string;
};

type LoginApiBody = {
  success?: boolean;
  message?: string;
  data?: unknown;
};

export type PatientLoginResult =
  | { ok: true; data: { requiresOtp: true; otpRequestId: string; userHint?: string } }
  | { ok: false; message: string };

export type VerifyOtpResult =
  | { ok: true; data?: { token: string; user: PatientUser } }
  | { ok: false; message: string };

function normalizePatientUser(user: PatientUser | null | undefined): PatientUser | null {
  if (!user) return null;
  const profile = (user.profile ?? {}) as Record<string, unknown>;
  return {
    ...user,
    nom: typeof user.nom === "string" ? user.nom : (profile.nom as string | undefined),
    prenom: typeof user.prenom === "string" ? user.prenom : (profile.prenom as string | undefined),
    nip: typeof user.nip === "string" ? user.nip : (profile.nip as string | undefined),
    date_naissance:
      typeof user.date_naissance === "string"
        ? user.date_naissance
        : (profile.date_naissance as string | undefined),
  };
}

function extractOtpRequestData(body: unknown): { otpRequestId?: string; userHint?: string } {
  let otpRequestId: string | undefined;
  let userHint: string | undefined;

  const visit = (node: unknown): void => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    const obj = node as Record<string, unknown>;
    if (!otpRequestId && typeof obj.otpRequestId === "string" && obj.otpRequestId.trim()) {
      otpRequestId = obj.otpRequestId.trim();
    }
    if (!userHint && typeof obj.userHint === "string" && obj.userHint.trim()) {
      userHint = obj.userHint.trim();
    }
    if (obj.data != null) visit(obj.data);
  };

  visit(body);
  return { otpRequestId, userHint };
}

function extractTokenAndUser(body: unknown): { token?: string; user?: PatientUser } {
  let token: string | undefined;
  let user: PatientUser | undefined;

  const visit = (node: unknown): void => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    const obj = node as Record<string, unknown>;
    const t = obj.accessToken ?? obj.token;
    if (typeof t === "string" && t.trim() && !token) token = t.trim();
    const candidateUser = obj.user;
    if (candidateUser && typeof candidateUser === "object" && !Array.isArray(candidateUser) && !user) {
      user = normalizePatientUser(candidateUser as PatientUser) ?? (candidateUser as PatientUser);
    }
    if (obj.data != null) visit(obj.data);
  };

  visit(body);
  return { token, user };
}

export async function login(nip: string): Promise<PatientLoginResult> {
  const out = await apiFetch<LoginApiBody>(BASE + "/login", {
    method: "POST",
    body: JSON.stringify({ nip }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  const { otpRequestId, userHint } = extractOtpRequestData(out.data);
  if (!otpRequestId) {
    return { ok: false, message: "Réponse OTP invalide: identifiant de requête manquant." };
  }
  return { ok: true, data: { requiresOtp: true, otpRequestId, userHint } };
}

export async function verifyOtp(otpRequestId: string, code: string): Promise<VerifyOtpResult> {
  const out = await apiFetch<unknown>(BASE + "/verify-otp", {
    method: "POST",
    body: JSON.stringify({ otpRequestId, otpCode: code }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  const { token, user } = extractTokenAndUser(out.data);
  if (token) setStoredToken(token);
  if (token && user) return { ok: true, data: { token, user } };
  return { ok: true };
}

type RefreshSuccessBody = {
  data?: { token?: string; accessToken?: string };
  token?: string;
  accessToken?: string;
};

export async function refreshToken(): Promise<{ ok: true; token: string } | { ok: false }> {
  const out = await apiFetch<RefreshSuccessBody>(BASE + "/refresh-token", {
    method: "POST",
    skipAuth: true,
  });
  if (!out.ok) return { ok: false };
  const { token } = extractTokenAndUser(out.data);
  if (typeof token === "string" && token.trim()) {
    setStoredToken(token);
    return { ok: true, token };
  }
  setStoredToken(null);
  return { ok: false };
}

export async function logout(): Promise<void> {
  await apiFetch(BASE + "/logout", { method: "POST", silent401: true });
  setStoredToken(null);
}

export async function getProfile(options?: {
  silent401?: boolean;
}): Promise<{ ok: true; data: ProfileResponse } | { ok: false; status: number; message: string }> {
  const out = await apiFetch<ProfileResponse>(BASE + "/profile", {
    silent401: options?.silent401,
  });
  if (!out.ok) return { ok: false, status: out.status, message: out.message };
  const payload = out.data as unknown as { data?: unknown } & Record<string, unknown>;
  const user = (payload?.data ?? payload) as PatientUser;
  return { ok: true, data: normalizePatientUser(user) ?? user };
}

export async function getSignupInfo(): Promise<
  { ok: true; data: PatientSignupInfo } | { ok: false; message: string }
> {
  const out = await apiFetch<PatientSignupInfo>(BASE + "/signup-info", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as unknown as { data?: PatientSignupInfo } & PatientSignupInfo;
  const info = (payload?.data ?? payload) as PatientSignupInfo;
  return { ok: true, data: info };
}

export type PatientSignupResult =
  | { ok: true; step?: string; otpRequestId?: string; data?: { user?: PatientUser } }
  | { ok: false; message: string };

export async function signup(body: PatientSignupBody): Promise<PatientSignupResult> {
  const out = await apiFetch<unknown>(BASE + "/signup", {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };

  const payload = out.data as Record<string, unknown>;
  const inner = ((payload?.data ?? payload) as Record<string, unknown>) ?? {};

  const step = typeof inner.step === "string" ? inner.step : undefined;
  const otpRequestId =
    typeof inner.otpRequestId === "string" && inner.otpRequestId.trim()
      ? inner.otpRequestId.trim()
      : undefined;
  const rawToken = inner.accessToken ?? inner.token;
  if (typeof rawToken === "string" && rawToken.trim()) setStoredToken(rawToken.trim());

  const user = inner.user ? normalizePatientUser(inner.user as PatientUser) ?? undefined : undefined;
  return { ok: true, step, otpRequestId, data: { user } };
}

function normalizeConsultation(item: unknown): PatientConsultation | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const raw = item as Record<string, unknown>;
  return {
    ...raw,
    id: typeof raw.id === "string" ? raw.id : undefined,
    patientNip: typeof raw.patientNip === "string" ? raw.patientNip : undefined,
    professionnelNip: typeof raw.professionnelNip === "string" ? raw.professionnelNip : undefined,
    etablissementId: typeof raw.etablissementId === "string" ? raw.etablissementId : undefined,
    date: typeof raw.date === "string" ? raw.date : undefined,
    typeConsultation: typeof raw.typeConsultation === "string" ? raw.typeConsultation : undefined,
    motif: typeof raw.motif === "string" ? raw.motif : undefined,
    observations: typeof raw.observations === "string" ? raw.observations : undefined,
    documents: Array.isArray(raw.documents) ? raw.documents : [],
    complement: raw.complement,
    statutConsultation:
      typeof raw.statutConsultation === "string"
        ? raw.statutConsultation
        : typeof raw.statut === "string"
          ? raw.statut
          : undefined,
    etatSortie: typeof raw.etatSortie === "string" ? raw.etatSortie : undefined,
    dateCloture:
      typeof raw.dateCloture === "string"
        ? raw.dateCloture
        : typeof raw.closedAt === "string"
          ? raw.closedAt
          : undefined,
  };
}

export async function getPatientConsultations(
  patientNip: string
): Promise<{ ok: true; data: PatientConsultation[] } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/consultations/patient/${encodeURIComponent(patientNip)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(out.data) ? out.data : [];
  return {
    ok: true,
    data: list.map((item) => normalizeConsultation(item)).filter((item): item is PatientConsultation => !!item),
  };
}

export async function getConsultationPdf(
  consultationId: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = (out.data as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
  const pdfUrl = payload?.pdfUrl ?? (out.data as Record<string, unknown>)?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

export async function getVaccinationCertificatePdf(
  patientNip: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/vaccinations/patient/${encodeURIComponent(patientNip)}/certificate`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = (out.data as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
  const pdfUrl = payload?.pdfUrl ?? (out.data as Record<string, unknown>)?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

export async function getMedicalFilePdf(
  patientNip: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = (out.data as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
  const pdfUrl = payload?.pdfUrl ?? (out.data as Record<string, unknown>)?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

export async function getPrescriptionPdf(
  ordonnanceId: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/ordonnances/${encodeURIComponent(ordonnanceId)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = (out.data as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
  const pdfUrl = payload?.pdfUrl ?? (out.data as Record<string, unknown>)?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

function normalizePrescription(item: unknown): PatientPrescription | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const raw = item as Record<string, unknown>;
  return {
    ...raw,
    id: typeof raw.id === "string" ? raw.id : undefined,
    patientNip: typeof raw.patientNip === "string" ? raw.patientNip : undefined,
    consultationId: typeof raw.consultationId === "string" ? raw.consultationId : undefined,
    professionnelNip: typeof raw.professionnelNip === "string" ? raw.professionnelNip : undefined,
    etablissementId: typeof raw.etablissementId === "string" ? raw.etablissementId : undefined,
    datePrescription: typeof raw.datePrescription === "string" ? raw.datePrescription : undefined,
    date: typeof raw.date === "string" ? raw.date : undefined,
    instructions: typeof raw.instructions === "string" ? raw.instructions : undefined,
    text: typeof raw.text === "string" ? raw.text : undefined,
    statut: typeof raw.statut === "string" ? raw.statut : undefined,
    medicaments: Array.isArray(raw.medicaments) ? raw.medicaments : [],
    documents: Array.isArray(raw.documents) ? raw.documents : [],
  };
}

export async function getPatientPrescriptions(
  patientNip: string
): Promise<{ ok: true; data: PatientPrescription[] } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/ordonnances/patient/${encodeURIComponent(patientNip)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(out.data) ? out.data : [];
  return {
    ok: true,
    data: list.map((item) => normalizePrescription(item)).filter((item): item is PatientPrescription => !!item),
  };
}

function normalizeVaccination(item: unknown): PatientVaccination | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const raw = item as Record<string, unknown>;
  return {
    ...raw,
    id: typeof raw.id === "string" ? raw.id : undefined,
    patientNip: typeof raw.patientNip === "string" ? raw.patientNip : undefined,
    professionnelNip: typeof raw.professionnelNip === "string" ? raw.professionnelNip : undefined,
    etablissementId: typeof raw.etablissementId === "string" ? raw.etablissementId : undefined,
    typeVaccin: typeof raw.typeVaccin === "string" ? raw.typeVaccin : undefined,
    libelleVaccin: typeof raw.libelleVaccin === "string" ? raw.libelleVaccin : undefined,
    vaccin: typeof raw.vaccin === "string" ? raw.vaccin : undefined,
    dateAdministration: typeof raw.dateAdministration === "string" ? raw.dateAdministration : undefined,
    dateVaccination: typeof raw.dateVaccination === "string" ? raw.dateVaccination : undefined,
    dose: typeof raw.dose === "string" ? raw.dose : undefined,
    lot: typeof raw.lot === "string" ? raw.lot : undefined,
    siteInjection: typeof raw.siteInjection === "string" ? raw.siteInjection : undefined,
    prochaineDose: typeof raw.prochaineDose === "string" || raw.prochaineDose == null ? (raw.prochaineDose as string | null) : undefined,
    prochaineDoseType:
      typeof raw.prochaineDoseType === "string" || raw.prochaineDoseType == null
        ? (raw.prochaineDoseType as string | null)
        : undefined,
  };
}

export async function getPatientVaccinations(
  patientNip: string
): Promise<{ ok: true; data: PatientVaccination[] } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/vaccinations/patient/${encodeURIComponent(patientNip)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(out.data) ? out.data : [];
  return {
    ok: true,
    data: list.map((item) => normalizeVaccination(item)).filter((item): item is PatientVaccination => !!item),
  };
}

export async function createPatientVaccination(
  body: CreatePatientVaccinationBody
): Promise<{ ok: true; data?: PatientVaccination } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>("/patient/vaccinations", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const item = normalizeVaccination(payload?.data ?? out.data);
  return { ok: true, data: item ?? undefined };
}

function normalizeMedicalFile(item: unknown): PatientMedicalFile | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const raw = item as Record<string, unknown>;
  const personalRaw =
    raw.informationsPersonnelles && typeof raw.informationsPersonnelles === "object" && !Array.isArray(raw.informationsPersonnelles)
      ? (raw.informationsPersonnelles as Record<string, unknown>)
      : {};
  const medicalRaw =
    raw.informationsMedicales && typeof raw.informationsMedicales === "object" && !Array.isArray(raw.informationsMedicales)
      ? (raw.informationsMedicales as Record<string, unknown>)
      : {};
  const snapshotRaw =
    raw.snapshot && typeof raw.snapshot === "object" && !Array.isArray(raw.snapshot)
      ? (raw.snapshot as Record<string, unknown>)
      : {};

  return {
    existe: typeof raw.existe === "boolean" ? raw.existe : true,
    id: typeof raw.id === "string" ? raw.id : undefined,
    numeroDossier: typeof raw.numeroDossier === "string" ? raw.numeroDossier : undefined,
    patientNip: typeof raw.patientNip === "string" ? raw.patientNip : undefined,
    statut: typeof raw.statut === "string" ? raw.statut : undefined,
    informationsPersonnelles: {
      nom: typeof personalRaw.nom === "string" ? personalRaw.nom : undefined,
      prenom: typeof personalRaw.prenom === "string" ? personalRaw.prenom : undefined,
      dateNaissance: typeof personalRaw.dateNaissance === "string" ? personalRaw.dateNaissance : undefined,
      adresse: typeof personalRaw.adresse === "string" ? personalRaw.adresse : undefined,
      email: typeof personalRaw.email === "string" ? personalRaw.email : undefined,
      telephone: typeof personalRaw.telephone === "string" ? personalRaw.telephone : undefined,
      profession: typeof personalRaw.profession === "string" ? personalRaw.profession : undefined,
    },
    informationsMedicales: {
      poids: typeof medicalRaw.poids === "number" ? medicalRaw.poids : undefined,
      taille: typeof medicalRaw.taille === "number" ? medicalRaw.taille : undefined,
      groupeSanguin: typeof medicalRaw.groupeSanguin === "string" ? medicalRaw.groupeSanguin : undefined,
      electrophorese: typeof medicalRaw.electrophorese === "string" ? medicalRaw.electrophorese : undefined,
      allergies: Array.isArray(medicalRaw.allergies) ? (medicalRaw.allergies.filter((x): x is string => typeof x === "string")) : [],
      intolerances: Array.isArray(medicalRaw.intolerances) ? (medicalRaw.intolerances.filter((x): x is string => typeof x === "string")) : [],
      vaccinations: Array.isArray(medicalRaw.vaccinations)
        ? medicalRaw.vaccinations
            .filter((x) => x && typeof x === "object" && !Array.isArray(x))
            .map((x) => {
              const v = x as Record<string, unknown>;
              return {
                type: typeof v.type === "string" ? v.type : undefined,
                date: typeof v.date === "string" ? v.date : undefined,
                professionnel: typeof v.professionnel === "string" ? v.professionnel : undefined,
              };
            })
        : [],
      antecedents: Array.isArray(medicalRaw.antecedents) ? medicalRaw.antecedents : [],
    },
    snapshot: {
      address: typeof snapshotRaw.address === "string" ? snapshotRaw.address : undefined,
      birth: typeof snapshotRaw.birth === "string" ? snapshotRaw.birth : undefined,
      electro: typeof snapshotRaw.electro === "string" ? snapshotRaw.electro : undefined,
      gs: typeof snapshotRaw.gs === "string" ? snapshotRaw.gs : undefined,
      poids: typeof snapshotRaw.poids === "number" ? snapshotRaw.poids : undefined,
      taille: typeof snapshotRaw.taille === "number" ? snapshotRaw.taille : undefined,
      allergies: typeof snapshotRaw.allergies === "string" ? snapshotRaw.allergies : undefined,
      intolerances: typeof snapshotRaw.intolerances === "string" ? snapshotRaw.intolerances : undefined,
      vaccins: Array.isArray(snapshotRaw.vaccins) ? snapshotRaw.vaccins.filter((x): x is string => typeof x === "string") : [],
      coverage:
        snapshotRaw.coverage === "verification" || snapshotRaw.coverage === "non" || snapshotRaw.coverage === "oui"
          ? snapshotRaw.coverage
          : undefined,
    },
  };
}

export async function getPatientMedicalFile(
  patientNip: string
): Promise<{ ok: true; data: PatientMedicalFile } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const file = normalizeMedicalFile(payload?.data ?? out.data);
  if (!file) return { ok: false, message: "Réponse dossier médical invalide." };
  return { ok: true, data: file };
}

/**
 * Met à jour l’entité patient (profil). Préféré pour le parcours patient sur le dossier.
 * Pour une mise à jour structurée uniquement via le dossier médical, voir `updatePatientMedicalFile` (usage legacy).
 */
export async function updatePatient(
  patientNip: string,
  body: UpdatePatientData
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(patientNip)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  return { ok: true, data: payload?.data ?? out.data };
}

/** @deprecated Préférer `updatePatient` pour les infos personnelles côté parcours patient (PUT `/patient/patients/:nip`). */
export async function updatePatientMedicalFile(
  patientNip: string,
  body: UpdatePatientMedicalFileBody
): Promise<{ ok: true; data: PatientMedicalFile } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const file = normalizeMedicalFile(payload?.data ?? out.data);
  if (!file) return { ok: false, message: "Réponse mise à jour dossier invalide." };
  return { ok: true, data: file };
}
